/**
 * screens/AssignmentRegister — Finance
 * ──────────────────────────────────────────────────────────────────────────
 * One row per custodian. Open a row for its ledger.
 *
 * THE GRID IS THE DESIGN'S, VERBATIM:
 *   minmax(0,1.7fr) minmax(0,0.75fr) minmax(0,0.9fr) minmax(0,1.3fr)
 *   minmax(0,0.85fr) minmax(0,0.8fr) 18px, gap 16px
 *
 * The `minmax(0, …)` is load-bearing and the handoff says so explicitly: with
 * bare `fr` tracks a long holder name widens one body column and not the
 * header, and the two grids visibly desynchronise. DataTable models `fr` as a
 * share of the LEFTOVER space only, never of content, which is what reproduces
 * it here.
 *
 * SEARCH AND SCOPE go to the server rather than filtering an already-loaded
 * array, because the `stale` flag on every row is computed against the org's
 * own staleDays at request time — so every row on the page agrees on what
 * "stale" means, including after a filter change.
 * ──────────────────────────────────────────────────────────────────────────
 */
import { useCallback, useMemo, useState } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import {
  Card,
  Chip,
  DataTable,
  EmptyState,
  Loading,
  Money,
  PageContainer,
  PageHeader,
  SearchInput,
  StatusPill,
  type Column,
} from '../../components';
import { neutral } from '../../theme/colors';
import { sans, mono, size as fontSize } from '../../theme/typography';
import { ellipsisStyle } from '../../theme/webStyle';
import { useResponsive } from '../../lib/responsive';
import { useAsyncData } from '../../lib/useAsyncData';
import { ageLabel, daysSince } from '../../lib/dates';
import { dotted, humanize } from '../../lib/format';
import { assignmentStatus, staleness } from '../../lib/status';
import { fetchAssignments } from '../../services/cash/cash.service';
import type { Assignment, Scope } from '../../schemas/cash';
import { useShell } from '../../context/ShellContext';
import { REGISTER_COPY, REGISTER_FLOW, REGISTER_STEPS } from './emptyState';

/** The scope filter chips, in the design's order. `all` is not a scope. */
const SCOPE_FILTERS: readonly { value: Scope | 'all'; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'open', label: 'Open' },
  { value: 'purpose', label: 'Purpose' },
  { value: 'collection', label: 'Collection' },
];

export function AssignmentRegisterScreen() {
  const { dataVersion, openDrawer, openModal } = useShell();
  const { isMobile } = useResponsive();

  const [query, setQuery] = useState('');
  const [scope, setScope] = useState<Scope | 'all'>('all');

  const load = useCallback(
    () =>
      fetchAssignments({
        ...(scope === 'all' ? {} : { scope }),
        ...(query.trim() ? { q: query.trim() } : {}),
      }),
    [scope, query],
  );

  const { data, loading } = useAsyncData(load, [scope, query], {
    version: dataVersion,
    errorTitle: 'Could not load the register',
  });

  const rows = data ?? [];

  const columns = useMemo<Column<Assignment>[]>(
    () => [
      {
        key: 'holder',
        header: 'Holder',
        track: { fr: 1.7 },
        stackedLabel: 'Holder',
        render: (row) => (
          <View style={styles.cellStack}>
            <Text numberOfLines={1} style={styles.holder}>
              {row.holder}
            </Text>
            {/*
              The design shows the stored slug as-is and lowercase —
              "employee · ppl_9120" — so underscores become spaces and nothing
              is capitalised. Sentence-casing it here would read "Employee",
              which the reference does not.
            */}
            <Text numberOfLines={1} style={styles.holderMeta}>
              {dotted(humanize(row.holder_type), row.holder_ref)}
            </Text>
          </View>
        ),
      },
      {
        key: 'scope',
        header: 'Scope',
        track: { fr: 0.75 },
        render: (row) => (
          <Text numberOfLines={1} style={styles.scope}>
            {row.scope}
          </Text>
        ),
      },
      {
        key: 'instrument',
        header: 'Instrument',
        track: { fr: 0.9 },
        render: (row) => (
          // "Purpose Wallet", "Petty Cash", "Card Linked" — the design applies
          // CSS `capitalize`, which title-cases every word.
          <Text numberOfLines={1} style={styles.instrument}>
            {humanize(row.instrument)}
          </Text>
        ),
      },
      {
        key: 'balance',
        header: 'Balance',
        track: { fr: 1.3 },
        align: 'right',
        render: (row) => <Money value={row.balance_minor} cur={row.cur} align="right" />,
      },
      {
        key: 'status',
        header: 'Status',
        track: { fr: 0.85 },
        render: (row) => {
          const state = assignmentStatus(row.status);
          return <StatusPill label={state.label} tone={state.tone} dense />;
        },
      },
      {
        key: 'reconciled',
        header: 'Reconciled',
        track: { fr: 0.8 },
        align: 'right',
        render: (row) => {
          // The age chip's colour is a DISPLAY ramp (≤7d green, ≤20d amber,
          // ≤60d orange, older or never red) and is deliberately separate from
          // the org's configurable staleDays, which drives the Overview KPI.
          const days = daysSince(row.last_recon_at);
          return (
            <StatusPill
              label={ageLabel(row.last_recon_at)}
              tone={staleness(days)}
              dense
              capitalize={false}
            />
          );
        },
      },
    ],
    [],
  );

  const filters = (
    <>
      <SearchInput
        value={query}
        onChangeText={setQuery}
        placeholder="Search holder or ref…"
        width={isMobile ? '100%' : 210}
      />
      <View style={styles.chips}>
        {SCOPE_FILTERS.map((filter) => (
          <Chip
            key={filter.value}
            label={filter.label}
            shape="pill"
            selected={scope === filter.value}
            onPress={() => setScope(filter.value)}
          />
        ))}
      </View>
    </>
  );

  if (loading && !data) {
    return (
      <PageContainer width="register">
        <Loading />
      </PageContainer>
    );
  }

  /**
   * A filtered-to-nothing register is NOT the empty state: the educational
   * panel would claim no cash has ever been assigned, which is false and
   * confusing. Only a genuinely empty, unfiltered register teaches.
   */
  const isFiltered = scope !== 'all' || query.trim().length > 0;
  const showEducational = rows.length === 0 && !isFiltered;

  return (
    <PageContainer width="register" testID="screen-register">
      <PageHeader
        title="Assignment register"
        subtitle="One row per custodian. Open a row for its ledger."
        right={filters}
      />

      {showEducational ? (
        <EmptyState
          eyebrow={REGISTER_COPY.eyebrow}
          title={REGISTER_COPY.title}
          blurb={REGISTER_COPY.blurb}
          nodes={REGISTER_FLOW}
          flowNote={REGISTER_COPY.flowNote}
          steps={REGISTER_STEPS}
          footNote={REGISTER_COPY.footNote}
          ctaLabel="Assign cash"
          onCta={() => openModal({ kind: 'assign' })}
        />
      ) : (
        <Card>
          <DataTable
            columns={columns}
            rows={rows}
            keyExtractor={(row) => row.id}
            onRowPress={(row) => openDrawer(row.id)}
            chevron
            empty={
              <View style={styles.noMatch}>
                <Text style={styles.noMatchText}>
                  No assignment matches that filter.
                </Text>
              </View>
            }
          />
        </Card>
      )}
    </PageContainer>
  );
}

const styles = StyleSheet.create({
  chips: { flexDirection: 'row', gap: 6, flexWrap: 'wrap' },
  cellStack: { minWidth: 0, gap: 1 },
  holder: { ...sans('500'), fontSize: fontSize.body, color: neutral.ink, ...ellipsisStyle },
  holderMeta: {
    ...mono('400'),
    fontSize: fontSize.tableHeader,
    color: neutral.grey400,
    ...ellipsisStyle,
  },
  scope: {
    ...sans('400'),
    fontSize: fontSize.label,
    color: neutral.ink,
    textTransform: 'capitalize',
    ...ellipsisStyle,
  },
  instrument: {
    ...sans('400'),
    fontSize: fontSize.label,
    color: neutral.grey600,
    // Title Case, as the design's `text-transform: capitalize` produces.
    textTransform: 'capitalize',
    ...ellipsisStyle,
  },
  noMatch: { paddingVertical: 34, alignItems: 'center' },
  noMatchText: { ...sans('400'), fontSize: fontSize.button, color: neutral.grey600 },
});

export default AssignmentRegisterScreen;
